'use client';

import React from 'react';
import { motion, type Variants } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/design-system';
import { LegalHero } from './LegalHero';
import { LegalContactInfo } from './LegalContactInfo';
import { legalContactData } from '@/lib/data/legal';
import { cn } from '@/lib/utils';

interface LegalPageProps {
  title: string;
  description?: string;
  heroBannerSrc?: string;
  heroBannerAlt?: string;
  lastUpdated?: string;
  children: React.ReactNode;
  className?: string;
  hideTitleInContent?: boolean;
  showContactInfo?: boolean;
}

/**
 * LegalPage Component
 * 
 * A premium layout component for all legal pages with elegant styling,
 * subtle animations, and semantic markup.
 */
export function LegalPage({ 
  title, 
  description,
  heroBannerSrc, 
  heroBannerAlt,
  lastUpdated, 
  children,
  className,
  hideTitleInContent,
  showContactInfo,
}: LegalPageProps) {
  
  // Content animation variants with reduced animation durations
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.05, 
        duration: 0.2 
      } 
    }
  };

  // Item animation variants
  const itemVariants: Variants = {
    hidden: { y: 10, opacity: 0 }, 
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        duration: 0.3, 
        ease: [0.22, 1, 0.36, 1]
      } 
    }
  };

  return (
    <main itemScope itemType="https://schema.org/WebPage" className={cn("w-full", className)}>
      <LegalHero
        title={title}
        description={description}
        imageSrc={heroBannerSrc}
        imageAlt={heroBannerAlt || `${title} Banner`}
        lastUpdated={lastUpdated}
      />
      
      <div className="flex justify-center w-full bg-background pt-4 pb-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="w-full max-w-5xl px-4 sm:px-6 lg:px-8"
        >
          <motion.div variants={itemVariants} className="w-full">
            <Card 
              className="w-full relative overflow-hidden premium-radius-2xl border-0 shadow-2xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl"
            >
              <div className="absolute -inset-2 z-0 rounded-[2rem] pointer-events-none bg-gradient-to-tr from-primary-400/60 via-pink-300/60 to-primary-200/60 dark:from-primary-700/60 dark:via-fuchsia-700/60 dark:to-primary-400/60 opacity-60 blur-xl animate-pulse-slow" />
              
              <CardContent className="relative p-6 md:p-8 lg:p-10">
                <article 
                  className={cn(
                    "prose prose-lg dark:prose-invert max-w-none",
                    "prose-headings:font-semibold prose-headings:text-foreground prose-p:text-muted-foreground",
                    "prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground",
                    "prose-ul:list-disc prose-ol:list-decimal",
                    "prose-blockquote:border-primary prose-blockquote:bg-muted/40 prose-blockquote:text-muted-foreground"
                  )}
                >
                  {children}
                </article>
                
                {showContactInfo && 
                  <LegalContactInfo {...legalContactData} className="mt-10 pt-10 border-t border-border" />
                }
                
                {lastUpdated && (
                  <div className="mt-10 pt-6 border-t border-border text-sm text-muted-foreground">
                    Last updated: {lastUpdated}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </main>
  );
}